import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  CodeDecodeMapListRequest,
  useFetchCodeDecodeMapsQuery,
} from '../codedecodemap-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
  Tooltip
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { CodeDecodeMapViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function CodeDecodeMapsScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'codedecodevalueid': 'codeDecodeValueName',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const codedecodemapTable: TableState = {
    columns: [
      {
        title: 'Id',
        dataIndex: '',
        key: 'Id',
        render: (codedecodemap: CodeDecodeMapViewModel) => (
          <>
              <Space>
                <span>{codedecodemap.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Code Decode Value',
        dataIndex: '',
        key: 'codedecodevalueid',
        render: (codedecodemap: CodeDecodeMapViewModel) => (
          <>
              <span>{codedecodemap.codeDecodeValueName}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('codedecodevalueid'),
      },
      {
        title: 'Map',
        dataIndex: '',
        key: 'mapid',
        render: (codedecodemap: CodeDecodeMapViewModel) => (
          <>
              <Tooltip title={codedecodemap.mapName} >
              <span>{codedecodemap.mapName}</span>
              </Tooltip>
          </>
        ),
      },
      {
        title: 'Customer',
        dataIndex: '',
        key: 'customerid',
        render: (codedecodemap: CodeDecodeMapViewModel) => (
          <>
              <span>{codedecodemap.customerName}</span>
          </>
        ),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (codedecodemap: CodeDecodeMapViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('CodeDecodeMapEdit', {
                      tenantId: params.tenantId,
                      Id: codedecodemap.id,
                      codedecodevalueid: params.codedecodevalueid
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="2"
                  disabled={!!(deletingId && deletingId === codedecodemap.id)}
                  onClick={() =>
                    deleteEntity(codedecodemap.id ?? 0, String(codedecodemap.codeDecodeValueId ?? ''))
                  }
                >
                  {deletingId === codedecodemap.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): CodeDecodeMapListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<CodeDecodeMapListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<CodeDecodeMapViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchCodeDecodeMapsQuery(pagination, { pollingInterval: DefaultPollingInterval });

  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'CodeDecodeMaps',
    tagType: 'CodeDecodeMap',
    entityLabel: 'code decode map',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('CodeDecodeMapEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const onBackToCodeDecodeType = () => {
    navigate(
      getLink('CodeDecodeTypeList', {
        ...params,
        tenantId: params.tenantId,
      })
    );
  };
  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CodeDecodeMapViewModel> | SorterResult<CodeDecodeMapViewModel>[],
    extra: TableCurrentDataSource<CodeDecodeMapViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<CodeDecodeMapViewModel>;

    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;

    // Check if we're actually changing sort or just pagination
    const isSortingAction = columnKey && order;
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort;
    
    if (isSortingAction) {
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'codedecodevalueid': 'codeDecodeValueName',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort
      newSortBy = currentSortBy || defaultSort;
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    const newUrl = getLink(
      'CodeDecodeMapList',
      { ...params },
      {
        sortBy: newSortBy,
        p: newPage,
        size: paginationConfig.pageSize,
        q: urlParams.get('q') || '',
      }
    );
    
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    navigate(
      getLink(
        'CodeDecodeMapList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q: encodeURIComponent(searchText?.length>0 ? `PMT.Name:*${searchText.replaceAll(" ", "\\ ")}* OR CUST.Name:*${searchText.replaceAll(" ", "\\ ")}*` : ''),
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };


  return (
    <div>
      <PageHeader
        extra={
            <Space>
                  <Button htmlType="button" data-testid={'create-new-codedecodemap'} onClick={onCreateNew}>
                  New Code Decode Map
                  </Button>
                </Space>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Code Decode Maps
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="codedecodemaps">
              <Link to={getLink('CodeDecodeMapList', { tenantId: params.tenantId,codedecodevalueid: params.codedecodevalueid })}>Code Decode Maps</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Input.Search
              placeholder="Search by Map Name or Customer Name"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
            />
            <Table
            scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={codedecodemapTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'CodeDecodeMap'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
